
import FilterBase from "./filterBase.js";

export default class Duotone extends FilterBase {
    objectType = "DuotoneFilterItem";
    _LowColor = "rgba(0, 0, 0, 1)";
    _HighColor = "rgba(255, 255, 255, 1)";
    _PosterizeLevel = 4;
    

    constructor(args = {}) {
        super(args);
        this.objectToClassValue(args);
    }

    objectToClassValue(args = {}) {
        if (args.tooltip == null) {
            args.tooltip = "Transforme l'image en duotone";
        }
        super.objectToClassValue(args);
        this._LowColor = args.lowColor ? args.lowColor : "rgba(255, 0, 0, 1)";
        this._HighColor = args.highColor ? args.highColor : "rgba(255, 255, 255, 1)";
        this._PosterizeLevel = args.posterizeLevel ? args.posterizeLevel : 4;
    }

    get LowColor() {
        return this._LowColor;
    }
    set LowColor(value) {
        if (this._LowColor != value) {
            this.invalidateFilterCache();
        }
        this._LowColor = value;
    }

    get HighColor() {
        return this._HighColor;
    }
    set HighColor(value) {
        if (this._HighColor != value) {
            this.invalidateFilterCache();
        }
        this._HighColor = value;
    }

    get PosterizeLevel() {
        return this._PosterizeLevel;
    }
    set PosterizeLevel(value) {
        value = parseInt(value);
        if (value < 1) {
            value = 1;
        }
        if (value > 24) {
            value = 24;
        }
        if (this._PosterizeLevel != value) {
            this.invalidateFilterCache();
        }
        this._PosterizeLevel = value;
    }


    get changeableProperty() {
        const parentProperty = super.changeableProperty;
        return [
            ...parentProperty.filter(el => el.type != "objectInput" && el.type != "arrayInput"),
            { text: "Couleur basse", name: "LowColor", type: "colorInput", dataType: "string", noAlpha: true },
            { text: "Couleur haute", name: "HighColor", type: "colorInput", dataType: "string", noAlpha: true },
            { text: "Niveau posterize", name: "PosterizeLevel", type: "rangeInput", min: 2, max: 24, step: 1, dataType: "number" },
            ...parentProperty.filter(el => el.type == "objectInput" || el.type == "arrayInput")
        ];
    }

    static get Name() {
        return "Duotone";
    }

    async filterAction(cv, cvImage) {
        // Implémentation basée sur la fonction Apply du fichier C#
        if (cvImage == null) {
            return cvImage;
        }

        try {
            // Convertir en niveaux de gris
            let gray = new cv.Mat();
            cv.cvtColor(cvImage, gray, cv.COLOR_RGBA2GRAY);

            // Appliquer un flou gaussien léger
            let blurred = new cv.Mat();
            cv.GaussianBlur(gray, blurred, new cv.Size(0, 0), 1.25);

            // Créer les LUT (Look-Up Tables) pour le duotone
            let lutB = new Array(256);
            let lutG = new Array(256);
            let lutR = new Array(256);
            let posterize = new Array(256);

            // Convertir les couleurs
            let lowColor = this.colorStringToRgbaObject(this.LowColor);
            let highColor = this.colorStringToRgbaObject(this.HighColor);

            let step = 256 / this.PosterizeLevel;

            // Créer les LUT
            for (let i = 0; i < 256; i++) {
                let t = i / 255.0;
                lutB[i] = this.lerpByte(lowColor.b, highColor.b, t);
                lutG[i] = this.lerpByte(lowColor.g, highColor.g, t);
                lutR[i] = this.lerpByte(lowColor.r, highColor.r, t);

                let val = Math.floor(i / step) * (255.0 / (this.PosterizeLevel - 1));
                posterize[i] = Math.max(0, Math.min(255, val));
            }

            // Appliquer la posterisation
            let posterized = new cv.Mat();
            this.applyLUT(cv, blurred, posterized, posterize);

            // Séparer en canaux B, G, R
            let b = new cv.Mat();
            let g = new cv.Mat();
            let r = new cv.Mat();

            this.applyLUT(cv, posterized, b, lutB);
            this.applyLUT(cv, posterized, g, lutG);
            this.applyLUT(cv, posterized, r, lutR);

            // Reconvertir en BGR
            let bgr = new cv.Mat();
            cv.cvtColor(posterized, bgr, cv.COLOR_GRAY2BGR);

            // Mélanger les canaux
            let channels = new cv.MatVector();
            channels.push_back(r);
            channels.push_back(g);
            channels.push_back(b);
            cv.merge(channels, bgr);

            // Copier le résultat dans l'image originale
            bgr.copyTo(cvImage);

            // Nettoyer la mémoire
            gray.delete();
            blurred.delete();
            posterized.delete();
            b.delete();
            g.delete();
            r.delete();
            bgr.delete();
            channels.delete();

            return cvImage;

        } catch (error) {
            console.error("Erreur dans le filtre duotone:", error);
            return cvImage;
        }
    }

    // Fonction utilitaire pour l'interpolation linéaire
    lerpByte(a, b, t) {
        return Math.round(a + (b - a) * t);
    }

    // Fonction pour appliquer une LUT (Look-Up Table)
    applyLUT(cv, src, dst, lut) {
        // Créer une matrice temporaire pour la LUT
        let lutMat = new cv.Mat(1, 256, cv.CV_8U);
        let lutData = new Uint8Array(256);
        for (let i = 0; i < 256; i++) {
            lutData[i] = lut[i];
        }
        lutMat.data.set(lutData);

        // Appliquer la LUT
        cv.LUT(src, lutMat, dst);

        // Nettoyer
        lutMat.delete();
    }

    static variableOfInstance() {
        return [
            ...super.variableOfInstance(),
            { property: "LowColor", construct: String, jsType: "String" },
            { property: "HighColor", construct: String, jsType: "String" },
            { property: "PosterizeLevel", construct: Number, jsType: "Number" },
        ];
    }

    toExportObject(variableArray = [], ressourceArray = []) {
        return {
            ...super.toExportObject(variableArray, ressourceArray),
            LowColor: this.rgbaStringToRgbString(this.LowColor),
            HighColor: this.rgbaStringToRgbString(this.HighColor),
            PosterizeLevel: this.PosterizeLevel,
        };
    }

    toObject(minify = false) {
        return {
            ...super.toObject(minify),
            lowColor: this.LowColor,
            highColor: this.HighColor,
            posterizeLevel: this.PosterizeLevel,
        };
    }
}